import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './utils/supabase';
import MainLayout from './layouts/MainLayout';
import Dashboard from './pages/Dashboard/Dashboard';
import Orders from './pages/Orders/Orders';
import Menu from './pages/Menu/Menu';
import Customers from './pages/Customers/Customers';
import Auth from './pages/Auth/Auth';
import Onboarding from './pages/Onboarding/Onboarding';
import Settings from './pages/Settings/Settings';
import BotSettings from './pages/BotSettings/BotSettings';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarded, setOnboarded] = useState(false);
  const [themeColor, setThemeColor] = useState('#16a34a');
  const checkingRef = useRef(false);

  const checkOnboarded = async (u) => {
    if (checkingRef.current) return;
    checkingRef.current = true;

    try {
      const { data } = await supabase
        .from('restaurants')
        .select('onboarded, theme_color')
        .eq('email', u.email)
        .maybeSingle();

      if (!data) {
        // No restaurant record yet — new user, show onboarding
        setOnboarded(false);
      } else {
        setOnboarded(data.onboarded === true);
        if (data.theme_color) setThemeColor(data.theme_color);
      }
    } catch {
      setOnboarded(false);
    } finally {
      checkingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    let initialized = false;

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      initialized = true;
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        await checkOnboarded(u);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip if getSession already handled initial load
      if (!initialized) return;

      const u = session?.user ?? null;

      if (event === 'SIGNED_OUT') {
        setUser(null);
        setOnboarded(false);
        setThemeColor('#16a34a');
        setLoading(false);
        return;
      }

      if (event === 'SIGNED_IN' && u) {
        setUser(u);
        await checkOnboarded(u);
        return;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--color-primary', themeColor);
  }, [themeColor]);

  const handleOnboardingComplete = (theme) => {
    if (theme) {
      setThemeColor(theme);
      document.documentElement.style.setProperty('--color-primary', theme);
    }
    // Setting onboarded=true is enough — the /onboarding route below re-evaluates
    // on this state change and automatically redirects to /dashboard.
    setOnboarded(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3 animate-pulse"
            style={{ backgroundColor: themeColor }}
          >
            <span className="text-white font-bold text-xl">R</span>
          </div>
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Everything below lives inside the router now — logged out, onboarding,
  // and the dashboard are all real routes, so the address bar always matches
  // what's on screen (e.g. signing out from /onboarding takes you to "/").

  return (
    <BrowserRouter>
      <Routes>
        {/* Onboarding wizard — its own URL, with guards both ways */}
        <Route
          path="/onboarding"
          element={
            !user
              ? <Navigate to="/" replace />
              : onboarded
                ? <Navigate to="/dashboard" replace />
                : <Onboarding user={user} onComplete={handleOnboardingComplete} />
          }
        />

        {/* Root: login screen, or the dashboard shell, depending on state */}
        <Route
          path="/"
          element={
            !user
              ? <Auth onLogin={(u) => setUser(u)} />
              : onboarded
                ? <MainLayout user={user} themeColor={themeColor} />
                : <Navigate to="/onboarding" replace />
          }
        >
          <Route index element={<Navigate to="/dashboard" />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="orders" element={<Orders />} />
          <Route path="menu" element={<Menu />} />
          <Route path="customers" element={<Customers />} />
          <Route path="settings" element={<Settings />} />
          <Route path="bot" element={<BotSettings />} />
        </Route>

        {/* Anything unrecognized -> the right place for the current state */}
        <Route
          path="*"
          element={
            <Navigate to={!user ? '/' : onboarded ? '/dashboard' : '/onboarding'} replace />
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;